import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { ManagedUserFilters, userService } from "@/services/users";
import { invitationService } from "@/services/invitations";
import { isNotFoundError } from "@/services/api";

/** A page past the end will not appear by asking again. */
const retryUnlessNotFound = (failureCount: number, error: unknown) =>
  !isNotFoundError(error) && failureCount < 3;

export function useManagedUsersQuery(filters: ManagedUserFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.managedUsers(filters),
    queryFn: () => userService.list(filters),
    enabled,
    retry: retryUnlessNotFound,
    placeholderData: keepPreviousData,
  });
}

/** Users and pending invitations per role, for the page title and the Pending invites chip. */
export function useUserCountsQuery() {
  return useQuery({ queryKey: queryKeys.userCounts(), queryFn: userService.counts });
}

/** Status and deletes also change who the underwriter picker, the broker search filter, and
 * the dashboard count. Refetch after a failure too: a refused delete means the row's state was
 * stale. */
function useUserMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => {
      for (const queryKey of [
        queryKeys.managedUsers(),
        queryKeys.userCounts(),
        queryKeys.underwriters(),
        queryKeys.brokersAll(),
        queryKeys.adminDashboardStats(),
        queryKeys.dashboardStats(),
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export const useUpdateManagedUser = () => useUserMutation(userService.update);
export const useDeleteManagedUser = () => useUserMutation(userService.delete);

export function usePendingInvitationsQuery(filters: ManagedUserFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.pendingInvitations(filters),
    queryFn: () => invitationService.listPending(filters),
    enabled,
    retry: retryUnlessNotFound,
    placeholderData: keepPreviousData,
  });
}

/** Every invitation change can move the pending list and its counts. */
function useInvitationMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pendingInvitations() });
      queryClient.invalidateQueries({ queryKey: queryKeys.userCounts() });
    },
  });
}

export const useCreateInvitation = () => useInvitationMutation(invitationService.create);
export const useUpdateInvitation = () => useInvitationMutation(invitationService.update);
export const useResendInvitation = () => useInvitationMutation(invitationService.resend);
export const useRevokeInvitation = () => useInvitationMutation(invitationService.revoke);
